import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import {
  Search,
  Loader2,
  Globe,
  Phone,
  Star,
  MapPin,
  Download,
  ShieldAlert,
  CheckCircle2,
} from "lucide-react";

/**
 * AdminPlacesImportTab
 * Search Google Places → tick candidates → bulk-import as unclaimed
 * businesses. Optionally auto-scrapes each website for a contact email
 * and immediately fires a claim invite (default ON).
 *
 * Backend:
 *   POST /api/admin/places/search  { query, location_bias, page_size }
 *   POST /api/admin/places/import  { place_ids, candidates, auto_send_claim_invites }
 */
export default function AdminPlacesImportTab() {
  const [query, setQuery] = useState("");
  const [locationBias, setLocationBias] = useState("");
  const [pageSize, setPageSize] = useState(15);
  const [searching, setSearching] = useState(false);
  const [importing, setImporting] = useState(false);
  const [autoInvite, setAutoInvite] = useState(true);

  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [lastImport, setLastImport] = useState(null);

  const runSearch = async (e) => {
    e?.preventDefault?.();
    if (!query.trim() || query.trim().length < 2) {
      toast.error("Enter at least 2 characters for the search query");
      return;
    }
    setSearching(true);
    setResults([]);
    setSelected(new Set());
    setLastImport(null);
    try {
      const r = await axios.post(
        `${API}/admin/places/search`,
        {
          query: query.trim(),
          location_bias: locationBias.trim() || null,
          page_size: Math.max(1, Math.min(20, Number(pageSize) || 10)),
        },
        { withCredentials: true }
      );
      const list = r.data?.results || [];
      setResults(list);
      // Pre-select everything not already imported
      const preselect = new Set(list.filter((p) => !p.already_imported).map((p) => p.place_id));
      setSelected(preselect);
      if (list.length === 0) toast.info("Google returned no results — try a broader query.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Google Places search failed");
    } finally {
      setSearching(false);
    }
  };

  const toggleAll = () => {
    if (selected.size === results.length) setSelected(new Set());
    else setSelected(new Set(results.filter((p) => !p.already_imported).map((p) => p.place_id)));
  };

  const toggleOne = (pid) => {
    const next = new Set(selected);
    if (next.has(pid)) next.delete(pid);
    else next.add(pid);
    setSelected(next);
  };

  const runImport = async () => {
    const ids = [...selected];
    if (ids.length === 0) {
      toast.error("Pick at least one business to import");
      return;
    }
    if (ids.length > 20) {
      toast.error("Google Places import caps at 20 per batch");
      return;
    }
    setImporting(true);
    try {
      const r = await axios.post(
        `${API}/admin/places/import`,
        {
          place_ids: ids,
          candidates: results.filter((p) => selected.has(p.place_id)),
          auto_send_claim_invites: autoInvite,
        },
        { withCredentials: true }
      );
      setLastImport(r.data);
      const c = r.data?.counts || {};
      toast.success(
        `Imported ${c.imported || 0} · Invites sent ${c.invites_sent || 0} · Skipped ${c.skipped || 0}`
      );
      // Re-mark imported ones so the UI reflects the new state
      setResults((prev) =>
        prev.map((p) =>
          selected.has(p.place_id) ? { ...p, already_imported: true } : p
        )
      );
      setSelected(new Set());
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
      <div className="p-6 border-b">
        <h2 className="font-heading text-xl text-tbc-navy flex items-center gap-2">
          <Globe className="w-5 h-5 text-tbc-gold" />
          Import from Google Places
        </h2>
        <p className="text-sm text-gray-500 font-body mt-1">
          Recruit new Black-owned businesses directly from Google. Imports land
          in the directory as <span className="font-medium">unclaimed</span>.
          When we can source a contact email we&apos;ll fire a claim invite
          automatically.
        </p>
      </div>

      {/* Search form */}
      <form onSubmit={runSearch} className="p-6 border-b space-y-4 bg-tbc-cream/40">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Search query *
            </label>
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder='e.g. "Black owned bakery" or "Black hair salon"'
              required
              minLength={2}
              maxLength={200}
              data-testid="places-search-query"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Location bias (optional)
            </label>
            <Input
              value={locationBias}
              onChange={(e) => setLocationBias(e.target.value)}
              placeholder="e.g. Atlanta, GA"
              maxLength={200}
              data-testid="places-search-location"
            />
          </div>
        </div>
        <div className="flex items-center flex-wrap gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Max results
            </label>
            <Input
              type="number"
              min={1}
              max={20}
              value={pageSize}
              onChange={(e) => setPageSize(e.target.value)}
              className="w-24"
            />
          </div>
          <div className="flex items-center gap-2 mt-6">
            <Switch
              id="auto-invite"
              checked={autoInvite}
              onCheckedChange={setAutoInvite}
              data-testid="places-auto-invite"
            />
            <label htmlFor="auto-invite" className="text-sm text-gray-700 font-body">
              Auto-send claim invite when we find a contact email
            </label>
          </div>
          <div className="ml-auto mt-6">
            <Button
              type="submit"
              disabled={searching}
              className="bg-tbc-navy hover:bg-tbc-navy/90 text-white"
              data-testid="places-search-submit"
            >
              {searching ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Search className="w-4 h-4 mr-2" />
              )}
              Search
            </Button>
          </div>
        </div>
      </form>

      {/* Results */}
      <div className="p-6">
        {results.length > 0 && (
          <div className="flex items-center justify-between mb-4">
            <label className="flex items-center gap-2 text-sm text-gray-700 font-body">
              <Checkbox
                checked={selected.size > 0 && selected.size === results.filter((r) => !r.already_imported).length}
                onCheckedChange={toggleAll}
                data-testid="places-select-all"
              />
              Select all importable ({selected.size} of {results.filter((r) => !r.already_imported).length})
            </label>
            <Button
              disabled={importing || selected.size === 0}
              onClick={runImport}
              className="bg-tbc-gold text-tbc-navy hover:bg-tbc-gold/90"
              data-testid="places-import-submit"
            >
              {importing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Import {selected.size} selected
            </Button>
          </div>
        )}

        {results.length === 0 && !searching && (
          <div className="p-8 text-center text-gray-500 font-body">
            No results yet — start a search above.
          </div>
        )}

        <div className="space-y-3">
          {results.map((p) => (
            <PlaceRow
              key={p.place_id}
              place={p}
              checked={selected.has(p.place_id)}
              onToggle={() => toggleOne(p.place_id)}
            />
          ))}
        </div>

        {lastImport && (
          <div className="mt-6 p-4 rounded-xl border border-emerald-200 bg-emerald-50">
            <div className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-1" />
              <div className="text-sm text-emerald-900 font-body">
                <div className="font-medium">Import complete</div>
                <div>Imported: {lastImport.counts?.imported || 0} · Invites sent: {lastImport.counts?.invites_sent || 0} · Skipped: {lastImport.counts?.skipped || 0}</div>
                {(lastImport.skipped || []).length > 0 && (
                  <ul className="mt-2 text-xs list-disc pl-5">
                    {lastImport.skipped.slice(0, 5).map((s) => (
                      <li key={`${s.place_id || "noplace"}-${s.reason}`}>{s.reason} — {s.place_id}</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function PlaceRow({ place, checked, onToggle }) {
  return (
    <div
      className={`flex gap-3 p-4 border rounded-xl transition-colors ${
        place.already_imported
          ? "border-gray-200 bg-gray-50 opacity-70"
          : "border-gray-100 hover:bg-tbc-cream/30"
      }`}
      data-testid={`places-result-${place.place_id}`}
    >
      <div className="pt-1">
        <Checkbox
          checked={checked}
          onCheckedChange={onToggle}
          disabled={place.already_imported}
        />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="font-heading text-tbc-navy">{place.name}</h3>
          <span className="text-[10px] uppercase tracking-widest text-tbc-gold border border-tbc-gold/40 px-2 py-0.5 rounded-full font-body">
            {place.category}
          </span>
          {place.already_imported && (
            <span className="text-[10px] uppercase tracking-widest text-gray-500 border border-gray-300 px-2 py-0.5 rounded-full font-body flex items-center gap-1">
              <ShieldAlert className="w-3 h-3" /> Already imported
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-gray-600 font-body">
          {place.address && (
            <span className="flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              {place.address}
            </span>
          )}
          {place.phone && (
            <span className="flex items-center gap-1">
              <Phone className="w-3 h-3" />
              {place.phone}
            </span>
          )}
          {place.website && (
            <a
              href={place.website}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-tbc-gold hover:underline"
            >
              <Globe className="w-3 h-3" />
              website
            </a>
          )}
          {typeof place.rating === "number" && (
            <span className="flex items-center gap-1">
              <Star className="w-3 h-3 text-tbc-gold" />
              {place.rating.toFixed(1)} ({place.reviews_count || 0})
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
